import * as React from "react";
import { Link } from "react-router-dom";
import { Inbox } from "lucide-react";

import { getAgentRequestsWaiting } from "@/api/client";
import { IconTooltip, navIconClass } from "@/features/home/components/NavIcons";
import { useFeature } from "@/features/profile/useFeature";

/**
 * The nav's pointer at the agent-request inbox, badged with what is waiting (D-68).
 *
 * <p>It counts decisions, not history. A held hour is waiting on this host; a request their rules
 * turned away is not, and neither is a hold that lapsed unanswered. A badge that outlives the thing
 * it points at is worse than no badge, because it teaches people to stop looking.</p>
 *
 * <p>Polled on the same 30-second interval and focus events the connections bell uses. Hidden
 * entirely without the hosting capability: a permanently empty badge for somebody no agent can
 * reach is furniture.</p>
 */
export function AgentRequestsBell() {
  const marketplace = useFeature("A2A_MARKETPLACE");
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (marketplace !== "granted") return;
    let cancelled = false;
    const load = () =>
      getAgentRequestsWaiting()
        .then((n) => {
          if (!cancelled) setCount(n);
        })
        .catch(() => {
          /* transient — keep the last known count */
        });

    void load();
    const id = window.setInterval(load, 30_000);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [marketplace]);

  if (marketplace !== "granted") return null;

  const label =
    count > 0
      ? `Agent requests: ${count} waiting on you`
      : "Agent requests";

  return (
    <Link
      to="/agent-requests"
      aria-label={label}
      className="group relative inline-flex h-9 w-9 items-center justify-center rounded-xl text-brand-teal transition-colors hover:bg-muted"
    >
      <Inbox className={navIconClass} aria-hidden />
      {count > 0 && (
        <span className="absolute right-0.5 top-0.5 inline-flex min-w-[1.05rem] items-center justify-center rounded-full border-2 border-background bg-rose-500 px-1 text-[0.6rem] font-bold leading-4 text-white">
          {count > 9 ? "9+" : count}
        </span>
      )}
      <IconTooltip label="Agent requests" />
    </Link>
  );
}
